import React from 'react';
import styled, { css } from 'styled-components';

import { TSemanticTextColorKeys, TTextType } from '../HBText/HBText';
import { darken } from '../../utils/color.utils';
import colors, { TColorKeys } from '../../styles/colors';
import { vars } from '../../theme';
import { ILinkRole } from '../HBLink/HBLink.types';
import { TTextAlignment, TWeight } from '../HBText/HBText.types';
import { newTextStyles } from '../HBText/HBText.styles';
import { useLinkBehavior } from '../../hooks/useLinkBehavior/useLinkBehavior';
import type {
    HBLinkProps,
    HBLinkTarget
} from '../../hooks/useLinkBehavior/useLinkBehavior.types';
import {
    setIconFillColor,
    revertOutlineForFocusVisible
} from '../../styles/mixins';
import { MustIncludeOneOfBasicTestIdWithIdProps } from '../common/internal/test-id.types';
import { useBuildTestIdInternal } from '../../hooks/useBuildTestIdInternal';

export type TLinkColor = string | TColorKeys | TSemanticTextColorKeys;

export interface ILinkProps
    extends Omit<
        React.ComponentProps<'a'>,
        'role' | 'ref' | 'href' | 'target'
    > {
    /** Horizontal text alignment inside the link element. */
    align?: TTextAlignment;
    /**
     * Link color. Accepts a CSS color string, a legacy `colors.ts` key
     * (e.g. `textSecondary`, `red50`), or a semantic `text.*` key
     * (e.g. `text.secondary`, `text.danger`). Hover is auto-darkened by 30%.
     */
    color?: TLinkColor;
    /**
     * When true, dims the link to 25% opacity. A link with a destination (`to` or `href`) drops
     * the destination, stays focusable and sets `aria-disabled`, and a click on it does nothing
     * and never reaches a parent. Without a destination it also disables pointer events and sets
     * `aria-disabled`.
     */
    disabled?: boolean;
    /**
     * Where to open the link, as on a native anchor: `'_blank'` for a new tab, `'_self'` for the
     * same tab, `'_parent'`, `'_top'`, or the name of a browsing context. `'_blank'` defaults
     * `rel` to `'noopener noreferrer'`. Omit it to open in the same tab. Not applied to a
     * disabled link.
     */
    target?: React.HTMLAttributeAnchorTarget;
    /** When true, uses the accent color from the styled-components theme instead of the color prop. */
    isUsingAccentColor?: boolean;
    /** Semantic role: 'link' (default) renders standard link behavior, 'button' for interactive elements that look like links. */
    role?: ILinkRole;
    /** Typography style applied from the HBText type system (e.g., 'body14', 'title18'). */
    textType: TTextType;
    /** Font weight override. Accepts numeric values (400, 600, 700) or named aliases ('bold', 'semibold', 'light'). */
    weight?: TWeight;
    /** When true, removes the default underline text decoration. Takes precedence over underlineOnHover. */
    noUnderline?: boolean;
    /** When true, hides the underline by default and shows it only on hover. Ignored if noUnderline is set. */
    underlineOnHover?: boolean;
    /**
     * Where the link goes: a path or route object for an in-app destination, or a full URL for an
     * external one. `HBUIProvider`'s `resolveLink` decides which. In-app targets render through
     * `LinkComponent`, external ones a native anchor. Wins over `href`.
     */
    to?: HBLinkTarget;
    /**
     * A native anchor destination that is never routed. `to` wins when both are set.
     * @deprecated Use `to`.
     */
    href?: HBLinkProps['href'];
}

const semanticTextColors: Record<string, string> = Object.fromEntries(
    Object.keys(vars.colors.text).map(key => [
        `text.${key}`,
        vars.colors.text[key as keyof typeof vars.colors.text]
    ])
);

/** Resolve legacy keys, semantic `text.*` keys, or raw CSS color strings. */
function resolveLinkColor(propColor?: TLinkColor): string | undefined {
    if (!propColor) {
        return undefined;
    }
    if (semanticTextColors[propColor]) {
        return semanticTextColors[propColor];
    }
    if (propColor in colors) {
        return colors[propColor as TColorKeys];
    }
    return propColor;
}

interface LinkStyleProps {
    $textType: TTextType;
    $color?: TLinkColor;
    $align?: TTextAlignment;
    $weight?: TWeight;
    $isUsingAccentColor: boolean;
    $noUnderline?: boolean;
    $underlineOnHover?: boolean;
    $disabled?: boolean;
    $isLink: boolean;
    $hasOnClick: boolean;
}

// Styling props are transient so they never reach the DOM or a `LinkComponent`, which would
// forward them to its anchor.
const Link = styled.a<LinkStyleProps>`
    ${revertOutlineForFocusVisible}
    ${({
        $textType,
        $color: propColor,
        theme,
        $isUsingAccentColor,
        $disabled
    }) => {
        const styles = newTextStyles[$textType];

        const color =
            resolveLinkColor(propColor) ||
            ($isUsingAccentColor && theme?.styles?.accent_color);

        const hoverColor = color ? darken(color, 0.3) : 'inherit';

        return css`
            ${styles}
            color: ${color || 'inherit'};
            ${setIconFillColor(color)};

            ${!$disabled &&
            css`
                &:hover,
                &:focus,
                &:active {
                    color: ${hoverColor};
                    ${setIconFillColor(hoverColor)}
                }
            `}
        `;
    }};
    ${({ $align }) =>
        $align &&
        css`
            text-align: ${$align};
        `}
    ${({ $weight }) =>
        $weight &&
        css`
            font-weight: ${$weight};
        `}
    ${({ $disabled, $isLink }) =>
        $disabled &&
        css`
            opacity: 0.25;
            ${$isLink
                ? css`
                      cursor: not-allowed;
                  `
                : css`
                      pointer-events: none;
                  `}
        `}
    ${({ $disabled, $hasOnClick, $isLink }) =>
        !$disabled &&
        ($isLink || $hasOnClick) &&
        css`
            cursor: pointer;
        `}
    text-decoration: underline;
    ${({ $underlineOnHover }) =>
        $underlineOnHover &&
        css`
            text-decoration: none;
            &:hover {
                text-decoration: underline;
            }
        `}
    ${({ $noUnderline }) =>
        $noUnderline &&
        css`
            text-decoration: none;
            &:hover {
                text-decoration: none;
            }
        `}

    transition: color 0.2s ease-in;

    i {
        display: inline-flex;
    }
`;

const HBLink = (props: ILinkProps & MustIncludeOneOfBasicTestIdWithIdProps) => {
    const {
        align,
        children,
        disabled,
        isUsingAccentColor = false,
        role = 'link',
        tabIndex = 0,
        textType,
        weight,
        color,
        noUnderline,
        underlineOnHover,
        testId,
        testIdName,
        to,
        href,
        target,
        rel,
        download,
        onClick,
        ...baseProps
    } = props;

    const { id } = baseProps;

    const finalTestId = useBuildTestIdInternal({
        testId,
        identifier: testIdName || id,
        suffix: 'link',
        componentName: 'HBLink'
    });

    const link = useLinkBehavior({
        to,
        href,
        target,
        rel,
        download,
        onClick,
        disabled,
        // The hook ignores `role="button"` and warns, so it never sees HBLink's legacy role.
        role: role === 'button' ? undefined : role,
        tabIndex,
        componentName: 'HBLink'
    });

    return (
        <Link
            {...baseProps}
            {...(link.isLink
                ? link.linkProps
                : {
                      tabIndex,
                      onClick,
                      target,
                      rel,
                      download,
                      ...(disabled && { 'aria-disabled': true })
                  })}
            role={role}
            $align={align}
            $color={color}
            $disabled={disabled}
            $isUsingAccentColor={isUsingAccentColor}
            $noUnderline={noUnderline}
            $underlineOnHover={underlineOnHover}
            $textType={textType}
            $weight={weight}
            $hasOnClick={!!onClick}
            data-testid={finalTestId}
            $isLink={link.isLink}
            as={link.isLink ? link.as : 'a'}
        >
            {children}
        </Link>
    );
};

export default HBLink;
